import { withViewComponent } from '@updater-legacy/components/custom-element';
import '@updater-legacy/components/popup';
import styles from './index.css';

const KEY_MAP = {
  13: 'Enter',
  32: 'Space',
  39: 'ArrowRight',
  37: 'ArrowLeft',
  38: 'ArrowUp',
  40: 'ArrowDown',
};

const itemsContainingNameWithSubstring = (items, value) => {
  const lowerCaseValue = value.toLowerCase();
  return items.filter((item) =>
    item.name.toLowerCase().includes(lowerCaseValue)
  );
};

const nextSelected = (options, filter, selected, step) => {
  const items = itemsContainingNameWithSubstring(options, filter);
  const currentIndex = items.findIndex((item) => item.name === selected);
  let newIndex = currentIndex + step;

  if (newIndex < 0) {
    newIndex = items.length - 1;
  }

  if (newIndex >= items.length) {
    newIndex = 0;
  }

  return items[newIndex].name;
};

const previous = (...args) => nextSelected(...args, -1);
const next = (...args) => nextSelected(...args, 1);

const handleKey = (options, key, filter, selected) => {
  if (options.length > 0) {
    switch (key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        return previous(options, filter, selected);
      case 'ArrowDown':
      case 'ArrowRight':
        return next(options, filter, selected);
      default:
        return selected;
    }
  }
  return selected;
};

const mark = (str, substr) => {
  return str.replace(new RegExp(substr, 'gi'), `<strong>$&</strong>`);
};

export default class Typeahead extends withViewComponent(styles)() {
  static props = ['options'];

  _state = {
    filter: '',
    selected: '',
  };

  disconnected() {
    this._removeListeners();
  }

  updated() {
    if (this.options) {
      this._removeListeners();

      const { filter } = this._state;
      const items = itemsContainingNameWithSubstring(this.options, filter);

      const { selected } = this._state;

      // prettier-ignore
      this.render`
        <div>
          <slot></slot>
          ${(items.length > 0 && filter.length > 0) ? this.wire()`
            <upd-popup open>
              <div class="container">
                ${items.map((item, index) => this.wire(item.key)`
                    <div
                      class=${['item', item.name === selected ? 'active' : ''].join(' ')}
                      onmouseenter=${() => this._handleMouseEnter(item.name)}
                      onclick=${() => this._handleClick(item.name)}
                    >
                      ${{ html: mark(item.name, filter) }}
                    </div>
                  `)}
              </div>
            </upd-popup>
          ` : ''}
        </div>
      `;

      this._addListeners();
    }
  }

  get input() {
    return this.querySelector('input');
  }

  _addListeners() {
    const input = this.input;
    if (input) {
      input.addEventListener('input', this._handleInput);
      input.addEventListener('keydown', this._handleKey);
    }
  }

  _removeListeners() {
    const input = this.input;
    if (input) {
      input.removeEventListener('input', this._handleInput);
      input.removeEventListener('keydown', this._handleKey);
    }
  }

  _handleInput = (event) => {
    const items = itemsContainingNameWithSubstring(
      this.options,
      event.target.value
    );
    this.mergeState({
      filter: event.target.value,
      selected: items.length > 0 ? items[0].name : '',
    });
  };

  _handleKey = (event) => {
    const key = KEY_MAP[event.keyCode];
    if (key === 'Enter' && this._state.selected !== '') {
      event.preventDefault();
      this._dispatchSelect(
        this.options.find((item) => item.name === this._state.selected)
      );
      this.mergeState({ filter: '', selected: '' });
    } else {
      this.mergeState({
        selected: handleKey(
          this.options,
          key,
          this._state.filter,
          this._state.selected
        ),
      });
    }
  };

  _handleMouseEnter = (selected) => {
    if (selected !== this._state.selected) {
      this.mergeState({ selected });
    }
  };

  _handleClick = (name) => {
    this._dispatchSelect(this.options.find((item) => item.name === name));
    this.mergeState({ filter: '', selected: '' });
  };

  _dispatchSelect = (item) => {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { item },
        bubbles: true,
        composed: true,
      })
    );
  };
}

customElements.define('upd-typeahead', Typeahead);
